/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class PropertiesPaneDOMV2
 * @hideconstructor
 * @classdesc API class that is used to manage Slideout functionality
 * (including the corrsponding view) across API calls
 */
define([
	'react-dom',
	'react',
	'mobx',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./PropertiesPaneDOMV2API',
	'./PropertiesView',
	'../../../dashboard/contentpane/PropertyUIControlsFactory',
	/** controls */
	'../../../dashboard/contentpane/controls/ColorPicker',
	'../../../dashboard/contentpane/controls/ComboBox',
	'../../../dashboard/contentpane/controls/DropDown',
	'../../../dashboard/contentpane/controls/FlyoutIconPicker',
	'../../../dashboard/contentpane/controls/FontCombo',
	'../../../dashboard/contentpane/controls/HintText',
	'../../../dashboard/contentpane/controls/InlineIconPicker',
	'../../../dashboard/contentpane/controls/InputLabel',
	'../../../dashboard/contentpane/controls/SingleLineLinks',
	'../../../dashboard/contentpane/controls/Slider',
	'../../../dashboard/contentpane/controls/Split',
	'../../../dashboard/contentpane/controls/ToggleButton',
	'../../../dashboard/contentpane/controls/ToggledCombo'
], function(
	ReactDOM,
	React,
	Mobx,
	APIFactory,
	PropertiesPaneDOMV2API,
	PropertiesView,
	PropertyUIControlsFactory,

	/** controls */
	ColorPicker,
	ComboBox,
	DropDown,
	FlyoutIconPicker,
	FontCombo,
	HintText,
	InlineIconPicker,
	InputLabel,
	SingleLineLinks,
	Slider,
	Split,
	ToggleButton,
	ToggledCombo
) {

	// TODO: temporary here; in the future will dynamically load
	// control type (key) => path to component (value);
	const BUILTIN_CONTROLS = {
		CollapsiblePicker: FlyoutIconPicker,
		ColorPicker,
		ComboBox,
		DropDown,
		FontCombo,
		HintText,
		IconPicker: InlineIconPicker,
		InputLabel,
		SingleLineLinks,
		Split,
		ToggleButton,
		ToggledCombo,
		UiSlider: Slider
	};

	class PropertiesPaneDOMV2 {

		constructor({ features }) {
			this._features = features;
			this._api = APIFactory.createAPI(this, [PropertiesPaneDOMV2API]);
			this._mobxDisposeCallbacks = [];

			this._controlFactory = null;

			if (this.isEnabled()) {
				this._features['InAppSlideoutDOM'].registerView(this.getProviderId(), this._api);
			}
		}

		getAPI() {
			return this._api;
		}

		isEnabled() {
			const featureChecker = this._features['API'].getGlassCoreSvc('.FeatureChecker');
			return !!(featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled'));
		}

		getProviderId() {
			return this._features['PropertiesPane'].getProviderId();
		}

		render(options) {
			this._features['InternalProperties'].activate();
			const { parentNode } = options;
			return new Promise((resolve) => {
				ReactDOM.render(
					<PropertiesView
						context={this._features['InternalProperties'].getContext()}
						factory={this._getControlFactory()}
						onNavigateBack={this._onNavigateBack}
					/>,
					parentNode,
					resolve
				);
			});
		}

		_getControlFactory() {
			if (!this._controlFactory) {
				this._controlFactory = new PropertyUIControlsFactory({
					controls: BUILTIN_CONTROLS,
					dashboard: this._features['API'],
					paletteService: this._features['Palette'],
					colorsService: this._features['Colors']
				});
			}
			return this._controlFactory;
		}

		close() {
			this._features['InternalProperties'].deactivate();
		}

		_onNavigateBack = () => {
			this._features['InternalProperties'].navigateBack();
		}

		destroy() {
			this._mobxDisposeCallbacks.forEach(cb => cb());
			this._mobxDisposeCallbacks = [];
			this._api = null;
			this._features = null;
		}
	}

	return PropertiesPaneDOMV2;
});
